import React from 'react';
import { Package, ShoppingCart, AlertTriangle, TrendingUp } from 'lucide-react';

const OverviewTab = ({ inventory, orders }) => {
  const recentOrders = orders?.slice(0, 5) || [];
  
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-sand-dark/20 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal/60 text-sm">Total Products</p>
              <p className="text-3xl font-bold text-charcoal mt-1">
                {inventory?.total_products || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center">
              <Package className="w-6 h-6 text-forest" />
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-sand-dark/20 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal/60 text-sm">Total Orders</p>
              <p className="text-3xl font-bold text-charcoal mt-1">
                {orders?.length || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-terracotta/10 flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-terracotta" />
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-sand-dark/20 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal/60 text-sm">Low Stock Items</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">
                {inventory?.low_stock_count || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-sand-dark/20 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-charcoal/60 text-sm">Total Revenue</p>
              <p className="text-3xl font-bold text-charcoal mt-1">
                ${orders?.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-forest" />
            </div>
          </div>
        </div>
      </div>

      {/* Low Stock Alert */}
      {inventory?.low_stock_count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5" />
            Low Stock Alert
          </h3>
          <div className="grid gap-3">
            {inventory.low_stock_items?.map(item => (
              <div key={item.id} className="flex justify-between items-center bg-white/80 rounded-xl p-4">
                <div>
                  <p className="font-medium text-charcoal">{item.name}</p>
                  <p className="text-sm text-charcoal/60">SKU: {item.sku}</p>
                </div>
                <div className="text-right">
                  <p className="text-amber-600 font-bold">{item.quantity_in_stock} left</p>
                  <p className="text-xs text-charcoal/60">Reorder at {item.reorder_threshold}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-sand-dark/20">
        <h3 className="font-semibold text-charcoal mb-4">Recent Orders</h3>
        <div className="space-y-3">
          {recentOrders.map(order => (
            <div key={order.id} className="flex justify-between items-center p-4 bg-sand/50 rounded-xl">
              <div>
                <p className="font-medium text-charcoal">
                  #{order.id.substring(0, 8).toUpperCase()}
                </p>
                <p className="text-sm text-charcoal/60">
                  {new Date(order.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold text-forest">${order.total?.toFixed(2)}</p>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                  order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                  order.status === 'processing' ? 'bg-amber-100 text-amber-700' :
                  order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {order.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
